/**
 * PostCard — reusable card used in feed lists and detail preview.
 *
 * Renders author header (with workspace badge for owner/admin posts),
 * post type pill, title (article/ad), excerpt/body preview, media hero,
 * topics, engagement counters, and action buttons (like/dislike/comment/
 * bookmark/report/edit/delete depending on caller state).
 *
 * Two modes:
 *   layout="card"     — compact card for feed lists
 *   layout="detail"   — expanded card for post detail page
 *
 * Action visibility is controlled by `interactive` boolean — anonymous
 * public visitors get a read-only card with counts only.
 */
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Heart, ThumbsDown, ChatText, BookmarkSimple, Flag,
  YoutubeLogo, FilePdf, DotsThreeVertical, Buildings, PencilSimple, Trash,
  ShareNetwork, Eye, EyeSlash, ArrowClockwise,
} from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileImage } from "@/components/ui/file-image";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { sanitizePostHtml } from "@/lib/publishSanitize";

const POST_TYPE_STYLE = {
  story:         { label: "Story",         cls: "bg-amber-100 text-amber-800 border-amber-200" },
  article:       { label: "Article",       cls: "bg-sky-100 text-sky-800 border-sky-200" },
  advertisement: { label: "Ad",            cls: "bg-rose-100 text-rose-800 border-rose-200" },
};

const STATUS_STYLE = {
  draft:     { label: "Draft",    cls: "bg-slate-100 text-slate-700 border-slate-200" },
  hidden:    { label: "Hidden",   cls: "bg-amber-100 text-amber-800 border-amber-200" },
  delinked:  { label: "Delinked", cls: "bg-purple-100 text-purple-800 border-purple-200" },
  deleted:   { label: "Deleted",  cls: "bg-rose-100 text-rose-800 border-rose-200" },
};

function youtubeEmbed(url) {
  try {
    const u = new URL(url);
    const vid = u.searchParams.get("v") || (u.hostname.includes("youtu.be") ? u.pathname.slice(1) : "");
    if (!vid) return null;
    return `https://www.youtube.com/embed/${vid}`;
  } catch { return null; }
}

function stripHtml(html) {
  return String(html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const secs = Math.floor((Date.now() - t) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  if (secs < 2592000) return `${Math.floor(secs / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function PostCard({
  post, layout = "card", interactive = false, viewer = null,
  onReact, onBookmark, onReport, onEdit, onDelete, onOpen, onShare,
  onDelink, onRepublish,
}) {
  const [expanded, setExpanded] = useState(layout === "detail");
  const [pending, setPending] = useState(null);

  const type = POST_TYPE_STYLE[post.post_type] || POST_TYPE_STYLE.story;
  const bodyText = useMemo(() => stripHtml(post.body_html), [post.body_html]);
  const excerpt = post.excerpt || bodyText.slice(0, 240);

  const heroImage = (post.media || []).find(m => m.kind === "image");
  const heroYouTube = (post.media || []).find(m => m.kind === "youtube");
  const heroPdf = (post.media || []).find(m => m.kind === "pdf");

  const doAction = async (label, fn) => {
    setPending(label);
    try { await fn(); } finally { setPending(null); }
  };

  const isAuthor = viewer?.is_author || false;
  const canModerate = viewer?.can_moderate || false;
  const canDelink = viewer?.can_delink || false;
  const canRepublish = viewer?.can_republish || false;
  const myReaction = viewer?.reaction || null;
  const bookmarked = viewer?.bookmarked || false;
  const reported = viewer?.reported || false;

  return (
    <article
      data-testid={`post-card-${post.id}`}
      className={cn(
        "border border-border rounded-2xl bg-card overflow-hidden transition-shadow",
        layout === "card" ? "hover:shadow-md cursor-pointer" : "shadow-sm"
      )}
      onClick={() => layout === "card" && onOpen?.(post)}
    >
      {/* Media hero */}
      {(heroImage || heroYouTube) && (
        <div className="relative w-full aspect-[16/9] bg-muted overflow-hidden">
          {heroYouTube ? (
            <iframe
              className="w-full h-full"
              src={youtubeEmbed(heroYouTube.url)}
              title="YouTube video"
              frameBorder="0"
              allow="accelerometer; autoplay; encrypted-media; gyroscope"
              allowFullScreen
              onClick={(e) => e.stopPropagation()}
            />
          ) : heroImage.file_id ? (
            <FileImage fileId={heroImage.file_id} alt={heroImage.caption || post.title || ""}
                       className="w-full h-full object-cover" />
          ) : (
            <img src={heroImage.url} alt={heroImage.caption || ""}
                 className="w-full h-full object-cover" />
          )}
        </div>
      )}

      <div className="p-4 sm:p-5 space-y-3">
        {/* Header */}
        <header className="flex items-start gap-3">
          <div className="w-10 h-10 shrink-0 rounded-full bg-muted overflow-hidden flex items-center justify-center border border-border">
            {post.author_photo_file_id ? (
              <FileImage fileId={post.author_photo_file_id} alt=""
                         className="w-full h-full object-cover"
                         fallback={<span className="text-sm font-semibold">{(post.author_name || "?")[0]}</span>} />
            ) : (
              <span className="text-sm font-semibold text-muted-foreground">
                {(post.author_name || "?")[0].toUpperCase()}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 text-sm">
              <span className="font-semibold text-foreground truncate">{post.author_name}</span>
              {post.author_role_display && (
                <span className="text-xs text-muted-foreground">· {post.author_role_display}</span>
              )}
              <Badge variant="outline" className={cn("text-[10px] uppercase tracking-wider", type.cls)}>
                {type.label}
              </Badge>
              {post.status !== "published" && STATUS_STYLE[post.status] && (
                <Badge variant="outline"
                       className={cn("text-[10px] uppercase tracking-wider", STATUS_STYLE[post.status].cls)}>
                  {STATUS_STYLE[post.status].label}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              {post.show_workspace && post.workspace_name && (
                <span className="inline-flex items-center gap-1">
                  <Buildings size={12} />
                  <span className="truncate">{post.workspace_name}</span>
                  <span>·</span>
                </span>
              )}
              <span>{timeAgo(post.published_at || post.created_at)}</span>
            </div>
          </div>

          {/* Overflow menu (author + moderator + delink/republish) */}
          {(isAuthor || canModerate || canDelink || canRepublish) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" onClick={(e) => e.stopPropagation()}
                        data-testid={`post-menu-${post.id}`}>
                  <DotsThreeVertical size={18} weight="bold" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {isAuthor && onEdit && (
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(post); }}
                                    data-testid={`post-edit-${post.id}`}>
                    <PencilSimple size={14} className="mr-2" /> Edit
                  </DropdownMenuItem>
                )}
                {/* Delink — available on published/hidden posts to the
                    author (Story/Article) or workspace admin/owner (Ad). */}
                {canDelink && ["published", "hidden"].includes(post.status) && onDelink && (
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDelink(post); }}
                                    data-testid={`post-delink-${post.id}`}>
                    <EyeSlash size={14} className="mr-2" /> Delink from public
                  </DropdownMenuItem>
                )}
                {/* Republish — available on delinked posts and drafts. */}
                {canRepublish && ["delinked", "draft"].includes(post.status) && onRepublish && (
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onRepublish(post); }}
                                    className="text-emerald-700"
                                    data-testid={`post-republish-${post.id}`}>
                    <ArrowClockwise size={14} className="mr-2" />
                    {post.status === "delinked" ? "Republish" : "Publish"}
                  </DropdownMenuItem>
                )}
                {(isAuthor || canModerate) && onDelete && (
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDelete(post); }}
                                    className="text-rose-600"
                                    data-testid={`post-delete-${post.id}`}>
                    <Trash size={14} className="mr-2" /> Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </header>

        {/* Title */}
        {post.title && (
          <h3 className={cn(
            "font-semibold text-foreground leading-tight",
            layout === "detail" ? "text-2xl sm:text-3xl" : "text-lg sm:text-xl"
          )}>
            {post.title}
          </h3>
        )}

        {/* Body */}
        {layout === "detail" || expanded ? (
          <div
            className="prose prose-sm sm:prose-base max-w-none dark:prose-invert"
            dangerouslySetInnerHTML={{ __html: sanitizePostHtml(post.body_html) }}
          />
        ) : (
          <p className="text-sm text-muted-foreground line-clamp-3">
            {excerpt}
          </p>
        )}

        {/* PDF attachment */}
        {heroPdf && (
          <a
            href={heroPdf.file_id ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${heroPdf.file_id}` : heroPdf.url}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 text-sm text-sky-700 hover:text-sky-900 underline"
            onClick={(e) => e.stopPropagation()}
          >
            <FilePdf size={16} /> {heroPdf.caption || "View attachment"}
          </a>
        )}

        {/* Ad CTA */}
        {post.post_type === "advertisement" && post.cta?.url && (
          <a
            href={post.cta.url}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 rounded-full text-sm font-medium"
            onClick={(e) => e.stopPropagation()}
            data-testid={`post-cta-${post.id}`}
          >
            {post.cta.label || "Learn more"}
          </a>
        )}

        {/* Topics */}
        {(post.topics || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {post.topics.map(t => (
              <Badge key={t} variant="outline" className="text-[11px] font-normal bg-muted/50">
                #{t}
              </Badge>
            ))}
          </div>
        )}

        {/* Engagement bar */}
        <footer className="flex items-center gap-1 pt-2 border-t border-border/40" onClick={(e) => e.stopPropagation()}>
          {interactive ? (
            <>
              <Button
                variant="ghost" size="sm" disabled={pending === "like"}
                className={cn("gap-1.5", myReaction === "like" && "text-rose-600 font-semibold")}
                onClick={() => doAction("like", () => onReact?.(myReaction === "like" ? null : "like"))}
                data-testid={`post-like-${post.id}`}
              >
                <Heart size={16} weight={myReaction === "like" ? "fill" : "regular"} />
                <span className="text-xs">{post.likes_count || 0}</span>
              </Button>
              <Button
                variant="ghost" size="sm" disabled={pending === "dislike"}
                className={cn("gap-1.5", myReaction === "dislike" && "text-slate-700 font-semibold")}
                onClick={() => doAction("dislike", () => onReact?.(myReaction === "dislike" ? null : "dislike"))}
                data-testid={`post-dislike-${post.id}`}
              >
                <ThumbsDown size={16} weight={myReaction === "dislike" ? "fill" : "regular"} />
                <span className="text-xs">{post.dislikes_count || 0}</span>
              </Button>
              <Button
                variant="ghost" size="sm"
                onClick={() => layout === "card" ? onOpen?.(post) : null}
                data-testid={`post-comment-${post.id}`}
              >
                <ChatText size={16} />
                <span className="text-xs">{post.comments_count || 0}</span>
              </Button>
              <div className="ml-auto flex items-center gap-1">
                <Button
                  variant="ghost" size="sm"
                  onClick={() => onShare?.(post)}
                  data-testid={`post-share-${post.id}`}
                  title="Copy link & share"
                >
                  <ShareNetwork size={16} />
                  <span className="text-xs">{post.shares_count || 0}</span>
                </Button>
                <Button
                  variant="ghost" size="sm" disabled={pending === "bookmark"}
                  className={cn(bookmarked && "text-sky-600 font-semibold")}
                  onClick={() => doAction("bookmark", () => onBookmark?.())}
                  data-testid={`post-bookmark-${post.id}`}
                >
                  <BookmarkSimple size={16} weight={bookmarked ? "fill" : "regular"} />
                </Button>
                <Button
                  variant="ghost" size="sm" disabled={reported}
                  onClick={() => onReport?.(post)}
                  data-testid={`post-report-${post.id}`}
                >
                  <Flag size={16} weight={reported ? "fill" : "regular"} />
                </Button>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1"><Heart size={14} /> {post.likes_count || 0}</span>
              <span className="inline-flex items-center gap-1"><ChatText size={14} /> {post.comments_count || 0}</span>
              <button
                onClick={(e) => { e.stopPropagation(); onShare?.(post); }}
                className="inline-flex items-center gap-1 hover:text-foreground"
                data-testid={`post-share-${post.id}`}
                title="Copy link"
              >
                <ShareNetwork size={14} /> {post.shares_count || 0}
              </button>
              <span className="ml-auto">Log in to react and comment</span>
            </div>
          )}
        </footer>

        {/* Expand link for card layout */}
        {layout === "card" && !expanded && bodyText.length > 240 && (
          <button
            className="text-xs text-sky-700 hover:text-sky-900 underline"
            onClick={(e) => { e.stopPropagation(); setExpanded(true); }}
          >
            Read more
          </button>
        )}
      </div>
    </article>
  );
}
